import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../context/AuthContext';
import { MAX_ADJUNTOS, useCrearTramite, useTramiteTipos } from '../../hooks/useTramites';
import AdjuntosInput from '../../components/AdjuntosInput';

export default function TramiteNuevo({ mostrarVolver }: { mostrarVolver: boolean }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { ficha } = useAuth();
  const { data: tipos, isLoading } = useTramiteTipos();
  const crear = useCrearTramite();

  const [tipoId, setTipoId] = useState('');
  const [detalle, setDetalle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ?tipo=<codigo> preselecciona el tipo (lo usan "Pedir corrección" y "Solicitar alta")
  const codigoPedido = params.get('tipo');
  useEffect(() => {
    if (!tipos || tipoId || !codigoPedido) return;
    const t = tipos.find((x) => x.codigo === codigoPedido);
    if (t) setTipoId(t.id);
  }, [tipos, codigoPedido, tipoId]);

  const tipo = (tipos ?? []).find((t) => t.id === tipoId);

  const handleEnviar = async () => {
    setError(null);
    if (!tipo) return setError('Elegí el tipo de trámite.');
    if (detalle.trim().length < 5) return setError('Contanos qué necesitás (al menos 5 caracteres).');
    if (tipo.requiere_adjunto && files.length === 0) return setError('Este trámite requiere adjuntar al menos un archivo.');
    try {
      const r = await crear.mutateAsync({ afiliadoId: ficha!.id, tipoId: tipo.id, detalle: detalle.trim(), files });
      navigate(`/tramites/${r.id}`, { state: { creado: `T-${String(r.numero).padStart(4, '0')}` }, replace: true });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Stack spacing={2}>
      {mostrarVolver && (
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/tramites')}>
            Volver a mis trámites
          </Button>
        </Box>
      )}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Nuevo trámite
        </Typography>
        <Stack spacing={2.5}>
          <TextField select label="Tipo de trámite" value={tipoId} onChange={(e) => setTipoId(e.target.value)} fullWidth required disabled={isLoading} helperText={tipo?.descripcion ?? ' '}>
            {(tipos ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.nombre}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Contanos qué necesitás"
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            required
            inputProps={{ maxLength: 2000 }}
            helperText={`${detalle.length}/2000`}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {tipo?.requiere_adjunto ? 'Adjuntos (obligatorio para este trámite)' : 'Adjuntos (opcional)'}
            </Typography>
            <AdjuntosInput files={files} onChange={setFiles} error={errorArchivo} onError={setErrorArchivo} disabled={crear.isPending} />
            <Typography variant="caption" color="text.secondary">
              Hasta {MAX_ADJUNTOS} archivos.
            </Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={handleEnviar} disabled={crear.isPending}>
              {crear.isPending ? 'Enviando…' : 'Enviar trámite'}
            </Button>
            <Button onClick={() => navigate('/tramites')} disabled={crear.isPending}>
              Cancelar
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
